/**
 * Web Vitals → PostHog pipe.
 *
 * Ships each Core Web Vital as a PostHog event once the browser reports
 * it. Gives us real-user-measured LCP / CLS / INP / TTFB / FCP straight
 * on the PostHog dashboard — a much truer signal than Lighthouse
 * synthetic runs.
 *
 * Bundle cost: 2KB (web-vitals is tree-shaken to the 5 metrics below).
 */
import type { Metric } from "web-vitals";

type PosthogWindow = Window & {
  posthog?: {
    capture: (event: string, props?: Record<string, unknown>) => void;
  };
};

const send = (metric: Metric) => {
  const w = window as PosthogWindow;
  if (!w.posthog?.capture) return;
  w.posthog.capture("$web_vital", {
    name: metric.name,
    value: Math.round(metric.value * 100) / 100,
    rating: metric.rating,           // "good" | "needs-improvement" | "poor"
    delta: Math.round(metric.delta * 100) / 100,
    id: metric.id,
    navigation_type: (metric as any).navigationType,
    path: window.location.pathname,
  });
};

export const initWebVitals = () => {
  // Late-import so the 2KB bundle doesn't block hydration.
  import("web-vitals")
    .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      onCLS(send);
      onFCP(send);
      onINP(send);
      onLCP(send);
      onTTFB(send);
    })
    .catch(() => {
      /* silently skip — a broken import must never block the app */
    });
};
